import { API_CONFIG } from '../config/api';
import { secureTokenManager } from '../utils/secureTokenManager';

// KPI Response Interfaces
export interface KPIData {
  overview: {
    totalServiceProviders: number;
    totalServiceUsers: number;
    totalServiceRequests: number;
    totalRatings: number;
    totalRevenue: string;
    generatedAt: string;
  };
  userMetrics: {
    serviceProviders: {
      total: number;
      active: number;
      pendingVerification: number;
      byStatus: {
        active: number;
        pending_verification: number;
        profile_completed: number;
        contact_verified: number;
        documents_uploaded: number;
      };
      byPaymentStatus: {
        completed: number;
        pending: number;
        not_required: number;
        expired: number;
        required_after_first_service: number;
      };
      newRegistrations: {
        today: number;
        thisWeek: number;
        thisMonth: number;
      };
      activeUsers: number;
    };
    serviceUsers: {
      total: number;
      newRegistrations: {
        today: number;
        thisWeek: number;
        thisMonth: number;
      };
      activeUsers: number;
    };
  };
  revenueAndPaymentMetrics: {
    totalRevenue: string;
    revenueByPeriod: {
      today: string;
      thisWeek: string;
      thisMonth: string;
    };
    subscriptions: {
      active: number;
      expired: number;
    };
    paymentStatusBreakdown: {
      completed: number;
      pending: number;
      not_required: number;
      expired: number;
      required_after_first_service: number;
    };
  };
}

export interface KPIResponse {
  success: boolean;
  data: KPIData;
}

export class KPIService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const authToken = secureTokenManager.getAdminSessionToken();

    // Remove /api prefix from endpoint if baseURL already includes it
    let normalizedEndpoint = endpoint;
    if (this.baseURL.endsWith('/api') && endpoint.startsWith('/api/')) {
      normalizedEndpoint = endpoint.substring(4); // Remove '/api' prefix
    } else if (this.baseURL === '/api' && endpoint.startsWith('/api')) {
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint.substring(3);
    }
    const url = `${this.baseURL}${normalizedEndpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data: T = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * Get KPI data from the admin endpoint
   */
  async getKPIs(): Promise<KPIResponse> {
    return this.makeRequest<KPIResponse>('/admin/kpis');
  }
}

// Export singleton instance
export const kpiService = new KPIService();
